<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { toast as sonnerToast } from 'vue-sonner';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';

// Cartão "SLA estourado" renderizado via `toast.custom(...)` (vue-sonner
// 2.0.9). Custom toast vem com `data-styled="false"`: o visual é todo daqui,
// no mesmo molde do ErrorToast (paleta n-ruby, X próprio).
const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
  // 'frt' | 'nrt' — resolvido no useSlaMissedQueue. RT não abre toast
  // (shouldShowSlaMissedToast).
  metric: {
    type: String,
    required: true,
  },
  // Id do toast no sonner (entrada da fila, único por abertura) pra fechar
  // este toast.
  toastId: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const getInbox = useMapGetter('inboxes/getInbox');
const notificationMeta = useMapGetter('notifications/getMeta');

const conversation = props.notification.primary_actor || {};
// Contato sem nome (canal de telefone/e-mail sem cadastro): mostra o e-mail
// ou o telefone; sem nenhum, fica só o #id da conversa ao lado.
const sender = conversation.meta?.sender || {};
const contactName = sender.name || sender.email || sender.phone_number || '';
// computed: o toast pode nascer antes de `inboxes/get` terminar (login →
// cable conecta antes do bootstrap da lista); sem isto ficaria em branco.
const inboxName = computed(
  () => getInbox.value(conversation.inbox_id)?.name || ''
);
const flaggedAt = messageStamp(props.notification.created_at, 'HH:mm');
const METRIC_LABELS = {
  frt: t('SLA.EVENTS.FRT'),
  nrt: t('SLA.EVENTS.NRT'),
};
// Parte vazia (inbox ainda fora do store) sai junto com o separador — sem
// isto a linha ficava "FRT ·  · sinalizado às".
const details = computed(() =>
  [
    METRIC_LABELS[props.metric],
    inboxName.value,
    t('SLA.MISSED_ALERT.FLAGGED_AT', { time: flaggedAt }),
  ]
    .filter(Boolean)
    .join(' · ')
);

const close = () => {
  sonnerToast.dismiss(props.toastId);
};

const openConversation = () => {
  const {
    id,
    primary_actor_id: primaryActorId,
    primary_actor_type: primaryActorType,
  } = props.notification;

  // Mesmo fluxo do sino (NotificationsView#openConversation): abrir = lida.
  store.dispatch('notifications/read', {
    id,
    primaryActorId,
    primaryActorType,
    unreadCount: notificationMeta.value.unreadCount,
  });
  close();
  // Conta da NOTIFICAÇÃO, não a da rota: é ela que diz de onde a conversa é.
  router.push(
    frontendURL(
      conversationUrl({
        accountId: props.notification.account_id,
        id: conversation.id,
      })
    )
  );
};
</script>

<template>
  <div
    class="flex w-full items-start gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-3 px-4 py-3"
  >
    <span
      class="i-lucide-alarm-clock-off mt-0.5 size-4 shrink-0 text-n-ruby-11"
    />
    <div class="flex min-w-0 flex-1 flex-col gap-1">
      <p class="m-0 text-xs font-medium uppercase tracking-wide text-n-ruby-11">
        {{ t('SLA.MISSED_ALERT.TITLE') }}
      </p>
      <!-- gap, não espaço no texto: o compilador do Vue descarta o espaço no
           fim do bloco e o nome colava no #id. Só o nome trunca — o #id é o
           que o atendente procura e não pode sumir com nome longo. -->
      <p class="m-0 flex min-w-0 gap-1 text-sm font-medium text-n-ruby-12">
        <span v-if="contactName" class="truncate">{{ contactName }}</span>
        <span class="shrink-0 font-normal text-n-ruby-11">
          {{ t('SLA.MISSED_ALERT.CONVERSATION_ID', { id: conversation.id }) }}
        </span>
      </p>
      <p class="m-0 truncate text-xs text-n-ruby-11">
        {{ details }}
      </p>
      <Button
        size="xs"
        color="ruby"
        class="mt-1 self-start"
        :label="t('SLA.MISSED_ALERT.OPEN_CONVERSATION')"
        type="button"
        @click="openConversation"
      />
    </div>
    <button
      v-tooltip.top="{
        content: t('GENERAL.CLOSE'),
        theme: 'tooltip-on-toast',
      }"
      type="button"
      class="-mr-1 -mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md text-n-ruby-11 transition-colors hover:bg-n-ruby-4 hover:text-n-ruby-12"
      :aria-label="t('GENERAL.CLOSE')"
      @click="close"
    >
      <span class="i-lucide-x size-4" />
    </button>
  </div>
</template>
